// Player-selectable table layouts (docs/ui-variants-design.md). A game may ship several presentations;
// the viewer picks one with `?ui=<id>`, a remembered choice, or the host's default. Every layout gets
// the same seat-filtered observation, so choosing never changes what a player may see or do.

import type { PresentationRef, PresentationVariantRef } from '../protocol/wire';

export type FormFactor = 'any' | 'phone' | 'desktop';

export interface LayoutOption {
  id: string;
  label: string;
  summary?: string;
  formFactor: FormFactor;
  presentation: PresentationRef;
}

export interface LayoutChoice {
  /** The layout to show; null when the game has no presentation at all. */
  id: string | null;
  /** The requested `?ui=` value that this game does not offer, so the page can say so. */
  unknown: string | null;
}

export const UI_PARAM = 'ui';
const STORAGE_PREFIX = 'bgc.ui.';

/** The offered layouts, or an empty list when the host announced a single presentation (the common case). */
export function layoutOptions(variants: readonly PresentationVariantRef[] | null | undefined): LayoutOption[] {
  if (!variants || variants.length < 2) return [];
  return variants.map(v => ({ id: v.id, label: v.label, ...(v.summary ? { summary: v.summary } : {}), formFactor: v.formFactor ?? 'any', presentation: v.presentation }));
}

/** The default layout is the one whose presentation is the announced top-level `presentation`. */
export function defaultLayoutId(options: readonly LayoutOption[], presentation: PresentationRef | null): string | null {
  if (options.length === 0) return null;
  return (presentation && options.find(o => o.presentation.sha256 === presentation.sha256)?.id) ?? options[0]!.id;
}

/**
 * Picks the layout: the URL request first, then the stored choice, then the default. An unknown request
 * falls back to the default and is reported; an unknown stored value falls back silently.
 */
export function chooseVariant(options: readonly { id: string }[], requested: string | null, stored: string | null, defaultId: string | null): LayoutChoice {
  const has = (id: string | null): id is string => id !== null && options.some(o => o.id === id);
  const fallback = has(defaultId) ? defaultId : options[0]?.id ?? null;
  if (requested !== null && requested !== '') {
    if (has(requested)) return { id: requested, unknown: null };
    return { id: fallback, unknown: requested };
  }
  if (has(stored)) return { id: stored, unknown: null };
  return { id: fallback, unknown: null };
}

export const storageKey = (gameId: string) => STORAGE_PREFIX + gameId;

export function readStoredLayout(gameId: string | null, storage?: Pick<Storage, 'getItem'>): string | null {
  if (!gameId) return null;
  try { return (storage ?? localStorage).getItem(storageKey(gameId)); } catch { return null; }
}

export function storeLayout(gameId: string | null, id: string, storage?: Pick<Storage, 'setItem'>): void {
  if (!gameId) return;
  try { (storage ?? localStorage).setItem(storageKey(gameId), id); } catch { /* optional viewer storage */ }
}

export function requestedLayout(search: string): string | null {
  return new URLSearchParams(search).get(UI_PARAM);
}

/**
 * The address-bar URL after choosing a layout: same path and other parameters, `ui` set, and an empty
 * fragment (a seat credential in it was already moved to storage).
 */
export function layoutUrl(location: { pathname: string; search: string }, id: string): string {
  const params = new URLSearchParams(location.search);
  params.set(UI_PARAM, id);
  const query = params.toString();
  return location.pathname + (query ? `?${query}` : '');
}

/** A player link with `?ui=<id>` placed before the fragment: `/rooms/r/games/g/play?ui=hand#seat=…&token=…`. */
export function withLayout(link: string, id: string | null): string {
  if (!id) return link;
  const hashAt = link.indexOf('#');
  const hash = hashAt < 0 ? '' : link.slice(hashAt);
  const rest = hashAt < 0 ? link : link.slice(0, hashAt);
  const queryAt = rest.indexOf('?');
  const path = queryAt < 0 ? rest : rest.slice(0, queryAt);
  const params = new URLSearchParams(queryAt < 0 ? '' : rest.slice(queryAt + 1));
  params.set(UI_PARAM, id);
  return `${path}?${params.toString()}${hash}`;
}

/** Short hint beside a layout name in the picker. */
export function formFactorHint(factor: FormFactor): string {
  return factor === 'phone' ? 'phone' : factor === 'desktop' ? 'large screen' : '';
}
